import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight } from "lucide-react"
import { toast } from "sonner"
import type { UpdateWorkspaceInput, Workspace } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Field, Heading, Hint, MiniAvatar, Pick, Section } from "@/components/workspace/primitives"
import { initials } from "@/lib/format"
import { SaveBar } from "@/components/workspace/save-bar"
import { api, ApiError } from "@/lib/api"
import { teamQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/general")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(teamQuery(params.workspace)),
  component: GeneralSettings,
})

const UNASSIGNED = "unassigned"

function supportedTimezones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone")
  } catch {
    return ["UTC", "Europe/London", "Europe/Paris", "America/New_York", "America/Los_Angeles", "Asia/Singapore"]
  }
}

function timezoneOptions(current: string) {
  const known = supportedTimezones()
  const zones = known.includes(current) ? known : [current, ...known]
  return zones.map((zone) => ({ value: zone, label: zone.replace(/_/g, " ") }))
}

/** Screen 39. */
function GeneralSettings() {
  const ctx = useWorkspaceContext()
  const can = useCan()
  const canEdit = can("manage_workspace")
  const queryClient = useQueryClient()
  const team = useSuspenseQuery(teamQuery(ctx.workspace.slug)).data
  const [name, setName] = useState(ctx.workspace.name)
  const [timezone, setTimezone] = useState(ctx.workspace.timezone)
  const [iconInitials, setIconInitials] = useState(ctx.workspace.iconInitials)
  const [editingIcon, setEditingIcon] = useState(false)
  const [assignment, setAssignment] = useState(ctx.workspace.assignmentDefault === "member" && ctx.workspace.assignmentDefaultUserId ? ctx.workspace.assignmentDefaultUserId : UNASSIGNED)
  const owner = team.members.find((m) => m.isOwner)

  const save = useMutation({
    mutationFn: (input: UpdateWorkspaceInput) => api<Workspace>(`/w/${ctx.workspace.slug}`, { method: "PATCH", body: JSON.stringify(input) }),
    onSuccess: async (workspace) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", ctx.workspace.slug] })
      await queryClient.invalidateQueries({ queryKey: ["me"] })
      toast.success("Workspace settings saved")
      setIconInitials(workspace.iconInitials)
      setEditingIcon(false)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not save changes"),
  })

  const dirty =
    name.trim() !== ctx.workspace.name ||
    timezone !== ctx.workspace.timezone ||
    iconInitials.trim().toUpperCase() !== ctx.workspace.iconInitials ||
    assignment !== (ctx.workspace.assignmentDefault === "member" && ctx.workspace.assignmentDefaultUserId ? ctx.workspace.assignmentDefaultUserId : UNASSIGNED)

  function submit() {
    save.mutate({
      name: name.trim(),
      timezone,
      iconInitials: (iconInitials.trim() || initials(name)).toUpperCase().slice(0, 2),
      assignmentDefault: assignment === UNASSIGNED ? "unassigned" : "member",
      assignmentDefaultUserId: assignment === UNASSIGNED ? null : assignment,
    })
  }

  return (
    <>
      <Heading title="General" description="Your workspace identity and conversation defaults." />
      <Section title="Workspace details" description="The name and defaults your team will see.">
        <div className="ws-workspace-identity">
          <Avatar className="size-14 rounded-xl">
            <AvatarFallback className="rounded-xl">{iconInitials || initials(name)}</AvatarFallback>
          </Avatar>
          <div>
            <strong>{name || ctx.workspace.name}</strong>
            <div>
              {editingIcon ? (
                <Input aria-label="Workspace icon initials" className="w-20" maxLength={2} value={iconInitials} autoFocus onChange={(e) => setIconInitials(e.target.value.toUpperCase())} onBlur={() => setEditingIcon(false)} />
              ) : (
                <Button variant="outline" size="sm" disabled={!canEdit} onClick={() => setEditingIcon(true)}>
                  Change icon
                </Button>
              )}
              <span>Workspace icon</span>
            </div>
          </div>
        </div>
        <div className="ws-form-grid">
          <Field label="Workspace name">{(id) => <Input id={id} value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />}</Field>
          <Pick label="Workspace timezone" value={timezone} options={timezoneOptions(timezone)} onChange={setTimezone} />
        </div>
        <p className="ws-hint">Your timezone is used for campaign schedules and report dates.</p>
      </Section>
      <Section title="Conversation defaults" description="Give every new conversation a clear starting point.">
        <div className="ws-form-grid">
          <Pick
            label="New conversation assignment"
            value={assignment}
            options={[{ value: UNASSIGNED, label: "Unassigned · visible to the team" }, ...team.members.map((m) => ({ value: m.userId, label: `Assign to ${m.name}` }))]}
            onChange={setAssignment}
          />
          <Hint title="Human control is protected">Taking over pauses automation. Only an explicit release gives it control again.</Hint>
        </div>
      </Section>
      <Section title="Workspace owner">
        <div className="ws-flex-between">
          {owner && (
            <div className="ws-person">
              <MiniAvatar name={owner.name} />
              <div>
                <strong>{owner.name}</strong>
                <small>{owner.email}</small>
              </div>
              <Badge variant="secondary">Owner</Badge>
            </div>
          )}
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/settings/team" params={{ workspace: ctx.workspace.slug }}>
              Manage team
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </Section>
      {canEdit ? (
        <SaveBar workspaceName={ctx.workspace.name} busy={save.isPending} disabled={!dirty || !name.trim()} onSave={submit} />
      ) : (
        <p className="ws-hint mt-6">Only admins can change workspace settings.</p>
      )}
    </>
  )
}
